// DESCARGO DE RESPONSABILIDAD antes de vincular WhatsApp (portado de la agenda, 12-oct-2026).
// Mismo texto legal (v1) y misma persistencia server-side por tenant (whatsapp_disclaimer).
// Mismo patrón de modal que ConfirmDialog (portal + overlay + panel; entra por abajo en móvil).
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { ShieldAlert, Loader2, X } from 'lucide-react';

interface Props {
  /** Se invoca al confirmar (ya con la casilla marcada). Debe persistir la aceptación. */
  onAccept: () => Promise<void> | void;
  onCancel: () => void;
  accepting?: boolean;
}

export default function WhatsAppDisclaimerModal({ onAccept, onCancel, accepting }: Props) {
  const [checked, setChecked] = useState(false);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-label="Antes de vincular tu WhatsApp">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={accepting ? undefined : onCancel} />
      <div className="modal-panel relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-line bg-surface shadow-xl sm:rounded-2xl">
        <div className="flex items-start justify-between gap-3 rounded-t-2xl bg-amber-500/10 px-5 py-4">
          <h3 className="flex items-center gap-2 font-display text-base font-bold text-content">
            <ShieldAlert size={20} className="shrink-0 text-amber-500" /> Antes de vincular tu WhatsApp
          </h3>
          <button onClick={onCancel} disabled={accepting} className="shrink-0 text-content-3 hover:text-content disabled:opacity-50" aria-label="Cerrar">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4 text-sm text-content-2">
          <p>
            Los mensajes se envían desde <b className="text-content">tu propio número</b> a través de una integración no
            oficial de WhatsApp. WhatsApp (Meta) <b className="text-content">no autoriza</b> el envío automatizado por esta
            vía, por lo que existe el <b className="text-content">riesgo de que tu número sea limitado, suspendido o
            bloqueado</b>, sobre todo si envías a muchos contactos en poco tiempo o a personas que no te conocen.
          </p>
          <div className="rounded-lg bg-surface-2 p-3">
            <p className="mb-1.5 font-medium text-content">Para reducir el riesgo te recomendamos:</p>
            <ul className="list-disc space-y-1 pl-5">
              <li>Usar un <b className="text-content">número dedicado al estudio</b>, no tu número personal principal.</li>
              <li><b className="text-content">No enviar mensajes masivos de golpe</b>; respeta los límites y los tiempos.</li>
              <li>Escribir solo a quien te dio su número y <b className="text-content">respetar a quien pida no recibir mensajes</b>.</li>
            </ul>
          </div>
          <p>
            <b className="text-content">No nos hacemos responsables</b> de la suspensión o bloqueo del número de WhatsApp que
            decidas vincular. Al continuar, confirmas que entiendes y aceptas este riesgo.
          </p>
          <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-line p-3 hover:bg-surface-2">
            <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} disabled={accepting}
              className="mt-0.5 h-4 w-4 rounded border-line" />
            <span className="text-content">He leído y acepto este descargo de responsabilidad.</span>
          </label>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-line px-5 py-4 sm:flex-row sm:justify-end">
          <button onClick={onCancel} disabled={accepting}
            className="rounded-lg border border-line bg-surface px-3.5 py-2 text-sm font-medium text-content-2 transition hover:bg-surface-2 disabled:opacity-50">
            Cancelar
          </button>
          <button onClick={() => onAccept()} disabled={!checked || accepting}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50">
            {accepting && <Loader2 size={16} className="animate-spin" />} Acepto y continúo
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
